/**
 * Embed mode: another site shows the portal in a frame. It opens at /embed or /embed/<page>, or at any public
 * page with ?embed=1 (the ask page, records, meetings, votes, the budget, the map and the rest).
 *
 * public/theme-init.js turns it on before the app loads: <html data-embed="true">, an /embed address
 * rewritten to its page with ?embed=1 (embedRewrite below is the same rule), and storage kept in memory where
 * the browser refuses it to a frame on another site. startEmbed() then keeps it on:
 * - every address inside the frame keeps ?embed=1 (router navigations and plain links alike), so a reload in
 *   the frame stays an embed, and the Worker keeps sending headers that let any site frame it
 *   (worker/lib/framing.ts);
 * - links that leave the portal, links to pages never shown in a frame (staff, /add), and links opened in a new
 *   tab go to a full page, never into the frame;
 * - auto height: when the host page includes /embed.js (public/embed.js), that script says hello; the frame then
 *   reports its content height to that page alone, and asks for its own set height while a dialog is open or a
 *   full-height screen (the map, a record) is shown. Without the script the frame keeps its set height and
 *   scrolls inside, like any frame.
 */

import { EMBED_PARAM, embedRewrite, fillsFrame, framable, fullPortalUrl, withEmbed } from './embedUrls';

export { EMBED_PARAM, embedRewrite, fillsFrame, framable, fullPortalUrl, withEmbed };

/** Whether this page is shown in embed mode (set by public/theme-init.js). */
export function isEmbedded(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.embed === 'true';
}

/** What the frame tells the host page (public/embed.js reads these, only from the portal's origin). */
export type EmbedMessage =
  | { type: 'vtp:ready'; v: 1 }
  | { type: 'vtp:size'; v: 1; height: number; fill: boolean; overlay: boolean; path: string }
  | { type: 'vtp:nav'; v: 1; path: string };

/** The height a content screen reports: its content, plus any open menu or list that hangs below it. */
function contentHeight(): number {
  const root = document.getElementById('root');
  let h = root ? root.getBoundingClientRect().height : document.documentElement.scrollHeight;
  document.querySelectorAll('.vc-panel, [role="listbox"]').forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.height) h = Math.max(h, r.bottom + window.scrollY + 12);
  });
  return Math.ceil(h);
}

/** A dialog that covers the page is open (the record preview, chat history, a meeting's files). */
function overlayOpen(): boolean {
  return document.body.style.overflow === 'hidden' || Boolean(document.querySelector('[aria-modal="true"]'));
}

let started = false;

/** Keeps embed mode on for the life of the page (see the top of this file). Does nothing outside embed mode. */
export function startEmbed(): void {
  if (started || !isEmbedded() || typeof window === 'undefined') return;
  started = true;
  const doc = document.documentElement;

  // 1. Every navigation inside keeps ?embed=1.
  for (const name of ['pushState', 'replaceState'] as const) {
    const original = history[name].bind(history);
    history[name] = (state: unknown, unused: string, url?: string | URL | null) => {
      original(state, unused, url == null ? url : withEmbed(String(url), window.location.href));
      queueMicrotask(() => navigated());
    };
  }
  window.addEventListener('popstate', () => navigated());

  // 2. Plain links: same-site pages keep ?embed=1; other sites, staff pages and new-tab opens leave the frame.
  document.addEventListener(
    'click',
    (e) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.hasAttribute('download')) return;
      let url: URL;
      try {
        url = new URL(a.href, window.location.href);
      } catch {
        return;
      }
      if (!/^https?:$/.test(url.protocol)) return;
      const newTab = e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1 || (a.target && a.target !== '_self');
      if (url.origin !== window.location.origin || !framable(url.pathname)) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        return;
      }
      if (newTab) a.href = fullPortalUrl(url.toString());
      else a.href = withEmbed(url.toString(), window.location.href);
    },
    true,
  );

  // 3. Auto height, once the host page's /embed.js says hello.
  let host: { win: Window; origin: string } | null = null;
  let last = '';
  const post = (msg: EmbedMessage) => {
    if (!host) return;
    try {
      host.win.postMessage(msg, host.origin);
    } catch {
      /* the host page went away */
    }
  };
  let frame = 0;
  const report = () => {
    frame = 0;
    if (!host) return;
    const overlay = overlayOpen();
    const fill = overlay || fillsFrame(window.location.pathname);
    const height = contentHeight();
    const key = `${height}|${fill}|${overlay}|${window.location.pathname}`;
    if (key === last) return;
    last = key;
    post({ type: 'vtp:size', v: 1, height, fill, overlay, path: window.location.pathname });
  };
  const soon = () => {
    if (!frame) frame = requestAnimationFrame(report);
  };
  // A new page (not a change of filters on the same one): the host page brings the frame's top into view.
  let shownPath = window.location.pathname;
  const navigated = () => {
    if (window.location.pathname !== shownPath) {
      shownPath = window.location.pathname;
      post({ type: 'vtp:nav', v: 1, path: shownPath });
    }
    soon();
  };
  window.addEventListener('message', (e) => {
    // Only the page that holds this frame, and only its hello: any site may embed the portal, and the frame then
    // talks to that site alone (an opaque origin, such as a file opened from disk, can only be answered with '*').
    if (window.parent === window || e.source !== window.parent) return;
    const d = e.data as { type?: unknown } | null;
    if (!d || d.type !== 'vtp:hello') return;
    host = { win: window.parent, origin: e.origin && e.origin !== 'null' ? e.origin : '*' };
    doc.dataset.embedAuto = 'true';
    last = '';
    soon();
  });
  const watch = () => {
    const root = document.getElementById('root');
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(soon);
      if (root) ro.observe(root);
      ro.observe(document.body);
    }
    new MutationObserver(soon).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'aria-modal', 'class'] });
    window.addEventListener('resize', soon);
  };
  if (document.body) watch();
  else document.addEventListener('DOMContentLoaded', watch, { once: true });
  // Tell the host page the frame is ready (it answers with hello if it runs /embed.js). Nothing private is sent.
  if (window.parent !== window) {
    try {
      window.parent.postMessage({ type: 'vtp:ready', v: 1 } satisfies EmbedMessage, '*');
    } catch {
      /* no parent to tell */
    }
  }
}
